import {
  MenuItem,
  MyAccountDropdown as MyAccountDropdownBase,
} from '@cambridgeassessment/cambridge-ui';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import { GACategory } from 'src/types';
import { sendGAEvent } from 'src/utils/googleAnalytics';
import { getFlatOrgsLength } from 'src/utils/transformers';
import { useAppContext } from '../AppContext';
import ChangeOrganisationDialog from '../ChangeOrganisationDialog/ChangeOrganisationDialog';
import { routes } from '../Pages';
// Cambridge-UI was built for React 16/17 — cast to include children for React 18 compatibility
const MyAccountDropdown = MyAccountDropdownBase as React.ComponentType<
  React.PropsWithChildren<Record<string, unknown>>
>;

const UserMenu = () => {
  const { appState, setAppState } = useAppContext();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [changeOrganisationOpen, setChangeOrganisationOpen] = useState(false);
  const { user, logout, isDelegate } = useAuth();
  const [photoUrl, setPhotoUrl] = useState(user.profilePicture);
  const history = useHistory();
  const options = useMemo(
    () =>
      [
        getFlatOrgsLength(user.organisations) > 1 && {
          value: 'Change organisation',
          onSelect: () => {
            setAppState((prevState) => ({
              ...prevState,
              currentPage: 'change_organization_' + Date.now(),
            }));
            setChangeOrganisationOpen(true);
            sendGAEvent(
              GACategory.userMenu,
              `${user.roles} clicked on user menu item Change organisation`
            );
            setDropdownOpen(false);
          },
        },
        {
          value: 'Manage account',
          onSelect: () => {
            setAppState((prevState) => ({
              ...prevState,
              currentPage: 'manage_account_' + Date.now(),
            })); //NOSONAR TODO: check if possible to use proper link
            history.push(routes.Account.path);
            sendGAEvent(
              GACategory.userMenu,
              `${user.roles} clicked on user menu item Manage account`
            );
            setDropdownOpen(false);
          },
        },
      ].filter(Boolean),
    [history, setChangeOrganisationOpen, logout, user.organisations, isDelegate]
  );

  useEffect(() => {
    setAppState((prevState) => ({
      ...prevState,
      profilePhotoUrl: user?.profilePicture,
    }));
  }, []);

  useEffect(() => {
    setPhotoUrl(appState?.profilePhotoUrl);
  }, [appState]);

  return (
    <>
      <MyAccountDropdown
        user={{
          name: user.fullName,
          imageSrc: photoUrl,
        }}
        open={dropdownOpen}
        onClick={(): void => setDropdownOpen(true)}
        onClose={(): void => setDropdownOpen(false)}
        onSignOutClick={(): void => {
          sendGAEvent(
            GACategory.userMenu,
            `${user.roles} clicked on user menu item Sign out`
          );
          logout();
        }}
        dataTestId="userMenu"
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            onClick={option.onSelect}
            data-testid={`menuOption-${option.value}`}
          >
            {option.value}
          </MenuItem>
        ))}
      </MyAccountDropdown>

      <ChangeOrganisationDialog
        open={changeOrganisationOpen}
        onClose={() => setChangeOrganisationOpen(false)}
      />
    </>
  );
};

export default memo(UserMenu);
